// Mock data for Fidelity Federal Associates LLC
// NOTE: All data here is mocked for the frontend-only build.

import { Shield, TrendingUp, Building2, ArrowUpRight, Scale, LineChart, KeyRound, Landmark, FileCheck2, Users, Lock, Compass, Droplets, Trees, Trash2 } from "lucide-react";

export const company = {
  name: "Fidelity Federal Associates",
  shortName: "FFA",
  legal: "Fidelity Federal Associates LLC",
  tagline: "Capital. Coverage. Custody.",
  contact: {
    principal: "Charlton Leland Nicholson",
    address: "401 Belmont Ave, Greenville, SC 29601",
    phone: "864-616-9331",
    email: "nicholsoncl2021@gmail.com",
    linkedin: "https://www.linkedin.com/in/leland-nicholson-ains-ais-1784a9367",
    photo: "/leland-headshot.jpg",
  },
};

export const navLinks = [
  { label: "Services", path: "/services/insurance", children: [
    { label: "Insurance", path: "/services/insurance" },
    { label: "Investing", path: "/services/investing" },
    { label: "Property Management", path: "/services/property" },
  ]},
  { label: "Company", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export const hero = {
  eyebrow: "Fidelity Federal Associates LLC",
  title: "Institutional discipline for private capital.",
  subtitle:
    "We build durable financial outcomes across insurance, investment strategy, and property stewardship \u2014 engineered with the rigor of an institution and the attention of a partner.",
  primaryCta: { label: "Explore Services", path: "/services/insurance" },
  secondaryCta: { label: "Speak with us", path: "/contact" },
};

export const stats = [
  { value: "3", label: "Integrated practice areas" },
  { value: "24/7", label: "Risk & portfolio monitoring" },
];

export const servicesOverview = [
  {
    id: "insurance",
    index: "01",
    icon: Shield,
    title: "Insurance",
    summary:
      "Risk architecture for individuals, families, and enterprises \u2014 from life and health to commercial casualty.",
    path: "/services/insurance",
  },
  {
    id: "investing",
    index: "02",
    icon: TrendingUp,
    title: "Investing",
    summary:
      "Disciplined portfolio strategy, retirement planning, and capital allocation aligned to long-horizon goals.",
    path: "/services/investing",
  },
  {
    id: "property",
    index: "03",
    icon: Building2,
    title: "Property Management",
    summary:
      "End-to-end stewardship of commercial and residential assets \u2014 leasing, operations, and performance.",
    path: "/services/property",
  },
];

export const approach = [
  {
    icon: Compass,
    title: "Strategy first",
    body: "Every engagement begins with a structured diagnostic \u2014 mapping risk, capital, and assets before a single recommendation is made.",
  },
  {
    icon: Lock,
    title: "Fiduciary by default",
    body: "Your interests anchor every decision. We operate with transparency, documented process, and accountability.",
  },
  {
    icon: Users,
    title: "One integrated team",
    body: "Insurance, investing, and property are managed as a single balance sheet \u2014 not three disconnected silos.",
  },
];

export const services = {
  insurance: {
    id: "insurance",
    index: "01",
    icon: Shield,
    name: "Insurance",
    headline: "Risk, architected.",
    intro:
      "We translate uncertainty into structured, defensible coverage. Our team designs protection programs that hold up under pressure \u2014 for households, professionals, and operating businesses.",
    capabilities: [
      { icon: Shield, title: "Life & Health", body: "Term, whole, and supplemental coverage calibrated to family and liquidity needs." },
      { icon: Building2, title: "Property & Casualty", body: "Home, auto, and asset protection with claims advocacy built in." },
      { icon: Scale, title: "Commercial Lines", body: "General liability, workers' comp, and tailored business owner policies." },
      { icon: FileCheck2, title: "Risk Advisory", body: "Annual coverage audits and exposure analysis to close hidden gaps." },
    ],
    process: [
      { step: "01", title: "Exposure mapping", body: "We inventory every insurable risk across your personal and commercial profile." },
      { step: "02", title: "Program design", body: "Coverage is engineered to your tolerance, not sold off a shelf." },
      { step: "03", title: "Carrier placement", body: "We source competitive terms across a vetted network of carriers." },
      { step: "04", title: "Ongoing stewardship", body: "Continuous review and claims support keep you protected as life changes." },
    ],
  },
  investing: {
    id: "investing",
    index: "02",
    icon: TrendingUp,
    name: "Investing",
    headline: "Capital, compounded.",
    intro:
      "We manage capital with institutional discipline \u2014 building diversified, tax-aware portfolios designed to perform across cycles, not headlines.",
    capabilities: [
      { icon: LineChart, title: "Portfolio Strategy", body: "Asset allocation grounded in your goals, horizon, and risk capacity." },
      { icon: TrendingUp, title: "Wealth Management", body: "Holistic oversight of investable assets, debt, and estate priorities." },
    ],
    process: [
      { step: "01", title: "Financial diagnostic", body: "A complete view of assets, liabilities, and long-term objectives." },
      { step: "02", title: "Allocation design", body: "A diversified blueprint matched to your time horizon and tolerance." },
      { step: "03", title: "Implementation", body: "Disciplined, cost-aware deployment across vetted vehicles." },
      { step: "04", title: "Rebalance & review", body: "Continuous monitoring and disciplined rebalancing through every cycle." },
    ],
  },
  property: {
    id: "property",
    index: "03",
    icon: Building2,
    name: "Property Management",
    headline: "Assets, stewarded.",
    intro:
      "We operate commercial and residential property as an investment \u2014 protecting value and removing the day-to-day burden of ownership.",
    mediaCapabilities: [
      {
        icon: Droplets,
        title: "Pressure Washing",
        body: "Restore patios, driveways, and walkways to like-new condition \u2014 removing years of grime, mold, and stains.",
        single: "https://customer-assets.emergentagent.com/job_fidelity-federal/artifacts/w0l035ug_image.png",
        splitLabel: true,
      },
      {
        icon: Trees,
        title: "Grounds Maintenance",
        body: "Overgrown lawns and unruly hedges transformed into crisp, manicured landscapes that elevate curb appeal.",
        single: "https://customer-assets.emergentagent.com/job_fidelity-federal/artifacts/1whdr5wk_image.png",
      },
      {
        icon: Trash2,
        title: "Daily Services",
        body: "Routine on-site care \u2014 from rolling trash bins to the curb weekly, window cleaning and upkeep that keeps properties pristine.",
        single: "https://images.pexels.com/photos/36086364/pexels-photo-36086364.jpeg",
      },
    ],
    capabilities: [
      { icon: KeyRound, title: "Leasing & Tenants", body: "Screening, placement, and retention for residential and commercial units." },
      { icon: Building2, title: "Operations", body: "Maintenance, vendor management, and 24/7 issue response." },
      { icon: LineChart, title: "Asset Performance", body: "Transparent reporting on NOI, occupancy, and capital improvements." },
      { icon: FileCheck2, title: "Compliance", body: "Lease enforcement, inspections, and regulatory adherence handled end-to-end." },
    ],
    process: [
      { step: "01", title: "Asset assessment", body: "We evaluate condition, market position, and income potential." },
      { step: "02", title: "Operating plan", body: "A tailored management strategy for occupancy and value growth." },
      { step: "03", title: "Active management", body: "Leasing, maintenance, and tenant relations executed daily." },
      { step: "04", title: "Owner reporting", body: "Clear monthly statements and proactive performance insight." },
    ],
  },
};

export const aboutValues = [
  { icon: Lock, title: "Integrity", body: "We hold to a fiduciary standard \u2014 your outcome is the only scorecard that matters." },
  { icon: Compass, title: "Discipline", body: "Process over impulse. We make decisions with structure, evidence, and patience." },
  { icon: ArrowUpRight, title: "Longevity", body: "We build relationships and portfolios designed to endure for generations." },
];

export const milestones = [
  { year: "Founding", body: "Established in Greenville, SC to unify insurance, investing, and property under one disciplined roof." },
  { year: "Practice", body: "Three integrated divisions serving individuals, families, and operating businesses." },
  { year: "Standard", body: "A fiduciary, transparency-first model that treats every client balance sheet as a single system." },
];

export const footerLinks = [
  { heading: "Services", links: [
    { label: "Insurance", path: "/services/insurance" },
    { label: "Investing", path: "/services/investing" },
    { label: "Property Management", path: "/services/property" },
  ]},
  { heading: "Company", links: [
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ]},
];
